<template>
  <mf-loading-dialog :loading="isLoadingCampaign">
    <base-page-layout :title="getPageTitle" :show-tour="!showCommunicateByChannel" flow-id="b151ccec-1d97-4e8a-a84f-5fb6d8ec3800">
      <template v-if="['ACTIVE', 'FINISHED'].includes(campaign.status)" v-slot:title-complement>
        <div class="pl-3">
          <mf-button label="Ver resultados do cupom" outlined text-transform="none" icon="monitoring" icon-position="left" @click="emitResultsFunction" />
        </div>
        <div class="pl-3">
          <mf-button label="Ver histórico de utilização" outlined text-transform="none" icon="history" icon-position="left" @click="emitHistoryFunction" />
        </div>
      </template>
      <template v-if="!showCommunicateByChannel">
        <coupon-discount-header :campaign="campaign" :validations="$v" />
        <coupon-discount-segment-toggle
          :campaign.sync="campaign"
          :segmented.sync="segmented"
          :validations="$v"
          :valid-coupon.sync="validCoupon"
          :loading="isLoadingCouponValidation"
          @loading="isLoadingCouponValidation = $event"
        />
        <coupon-discount-type :campaign="campaign" :segmented.sync="segmented" :validations="$v" />
        <mf-action-buttons
          data-userflow-id="step7"
          class="mt-2"
          :primary-button="primaryButton"
          :secondary-button="secondaryButton"
          :extra-button="deleteButton"
          :cancel-button="cancelButton"
        />
      </template>
      <template v-else>
        <communicate-by-channel v-if="this.benefitId" :benefitId="benefitId" />
      </template>
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import CommunicateByChannel from '../components/edit/CommunicateByChannel.vue'
import CouponDiscountHeader from '../components/edit/CouponDiscountHeader.vue'
import CouponDiscountSegmentToggle from '../components/edit/CouponDiscountSegmentToggle.vue'
import CouponDiscountType from '../components/edit/CouponDiscountType.vue'

import { deepDelete } from '@/helpers/deepDelete'
import { isFuture, notOnlySpace } from '@/helpers/validators.js'
import { DateTime } from 'luxon'
import Swal from 'sweetalert2'
import { maxLength, maxValue, minLength, minValue, required } from 'vuelidate/lib/validators'
import { DELETE_BENEFIT, FINISH_ACTIVE_BENEFIT, QUERY_FIND_BENEFIT_BY_ID, UNSCHEDULE_BENEFIT, UPSERT_BENEFIT, UPSERT_DRAFT_BENEFIT } from '../../graphql'

export default {
  name: 'CoupomDiscount',
  components: {
    BasePageLayout,
    CouponDiscountHeader,
    CouponDiscountSegmentToggle,
    CouponDiscountType,
    CommunicateByChannel,
  },
  data: () => ({
    tryingToSaveAsDraft: false,
    loading: false,
    isLoadingCampaign: false,
    isLoadingCouponValidation: false,
    loadingDraft: false,
    loadingActive: false,
    DateTime,
    validCoupon: null,
    segmented: true,
    showCommunicateByChannel: false,
    benefitId: null,
    campaign: {
      _id: null,
      title: '',
      active: false,
      description: '',
      appTitle: '',
      startDate: '',
      expiresAt: '',
      variableValidity: false,
      daysToUseBenefitAfterRegistration: 1,
      units: [],
      value: 0,
      minimumValue: 0,
      minimumProducts: 0,
      minimumTriggerProductsValue: 0,
      model: 'MANUAL',
      code: '',
      limitUse: 0,
      type: 'DISCOUNT_COUPON_ABSOLUTE',
      products: [],
      productsGroups: [
        {
          skus: null,
          groupId: null,
        },
      ],
      isAutoActivated: null,
      promoType: 'NO_TRIGGER_PRODUCTS',
      segments: [],
    },
  }),
  computed: {
    getPageTitle() {
      if (this.showCommunicateByChannel) {
        return 'Selecione como deseja comunicar este desconto'
      }
      return !this.isFinished ? 'Nova campanha' : 'Visualizar campanha'
    },
    isFinished() {
      return this.campaign.status === 'FINISHED' || this.campaign.status === 'ACTIVE'
    },
    primaryButton() {
      const button = {
        isVisible: true,
        isDisabled: this.isFinished,
        isLoading: this.loadingActive || this.isLoadingCouponValidation,
        action: () => this.saveCampaign(false),
      }

      switch (this.campaign.status) {
        case 'ACTIVE':
        case 'SCHEDULED':
          button.text = 'Salvar alterações'
          break
        default:
          button.text = 'Ativar campanha'
      }

      return button
    },

    secondaryButton() {
      const button = {
        isVisible: true,
        isDisabled: this.isFinished,
        isLoading: this.loadingDraft || this.isLoadingCouponValidation,
      }

      switch (this.campaign.status) {
        case 'ACTIVE':
          button.text = 'Finalizar'
          button.action = () => this.mutateBenefit('finish')
          break
        case 'SCHEDULED':
          button.text = 'Suspender'
          button.action = () => this.mutateBenefit('unschedule')
          break
        case 'DRAFT':
          button.text = 'Salvar alterações'
          button.action = () => this.saveCampaign(true)
          break
        default:
          button.text = 'Salvar rascunho'
          button.action = () => this.saveCampaign(true)
      }
      return button
    },

    cancelButton() {
      const button = {
        action: this.returnToCampaigns,
        isVisible: true,
        isDisabled: false,
        isLoading: this.loading || this.isLoadingCouponValidation,
      }

      switch (this.campaign.status) {
        case 'ACTIVE':
        case 'FINISHED':
          button.text = 'Fechar'
          break
        default:
          button.text = 'Cancelar'
      }

      return button
    },

    deleteButton() {
      return {
        text: 'Apagar rascunho',
        action: () => this.mutateBenefit('delete', this.campaign.title),
        isVisible: this.campaign.status === 'DRAFT',
        isDisabled: false,
        isLoading: this.loading || this.isLoadingCouponValidation,
      }
    },
    preparePayload() {
      const formatFloat = (value) => {
        if (typeof value === 'number') return value
        if (!value || typeof value !== 'string') return 0
        const formattedValue = value.replace('.', '')
        return parseFloat(formattedValue.replace(',', '.'))
      }

      this.normalizePromoTypeFields()

      const campaign = {
        ...this.campaign,
        ...(this.segmented ? { limitUse: 0, code: '', variableValidity: false } : { segments: [], isAutoActivated: false }),
        value: formatFloat(this.campaign.value),
        minimumValue: formatFloat(this.campaign.minimumValue),
        minimumTriggerProductsValue: formatFloat(this.campaign.minimumTriggerProductsValue),
        products: this.ungroupedProducts.map((product) => product.sku),
        productsGroups: this.groupedProducts.map((group) => {
          return {
            groupId: group._id,
            skus: group.products.map((product) => product.sku),
          }
        }),
      }
      if (!this.campaign.startDate || !this.campaign.expiresAt) {
        deepDelete(campaign, ['startDate', 'expiresAt'])
      }
      if (!this.campaign.variableValidity) {
        deepDelete(campaign, ['daysToUseBenefitAfterRegistration'])
      }
      deepDelete(campaign, ['__typename', 'status', 'image', 'brands', 'id'])
      return campaign
    },
    isFranchise() {
      return this.$getLoggedUser().is_franchise
    },
    isFranchisee() {
      return this.$getLoggedUser().is_franchisee
    },
    ungroupedProducts() {
      if (!this.campaign.products) return []
      return this.campaign.products.filter((product) => !product.isGroup)
    },
    groupedProducts() {
      if (!this.campaign.products) return []
      return this.campaign.products.filter((product) => product.isGroup)
    },
  },
  async mounted() {
    this.initAutoActivation()
    if (this.isFranchisee) this.returnToCampaigns()
    if (this.$route.query.communicate) this.showCommunicateByChannel = true
    const id = this.$route.params.id
    if (id) {
      await this.getData(id)
    }
  },
  methods: {
    returnToCampaigns() {
      this.$router.push('/campanhas/cupom-desconto')
    },
    initAutoActivation() {
      this.campaign.isAutoActivated = !this.$getAllowedModules()['mobile']
    },
    filterValidFields(payload) {
      const allowedFields = [
        '_id',
        'title',
        'appTitle',
        'variableValidity',
        'daysToUseBenefitAfterRegistration',
        'startDate',
        'expiresAt',
        'units',
        'value',
        'minimumValue',
        'minimumProducts',
        'model',
        'code',
        'limitUse',
        'type',
        'products',
        'productsGroups',
        'description',
        'segments',
        'isAutoActivated',
        'promoType',
        'minimumTriggerProductsValue',
        'active',
      ]

      return Object.fromEntries(Object.entries(payload).filter(([key]) => allowedFields.includes(key)))
    },
    normalizePromoTypeFields() {
      switch (this.campaign.promoType) {
        case 'NO_TRIGGER_PRODUCTS':
          this.campaign.minimumProducts = 0
          this.campaign.minimumTriggerProductsValue = 0
          this.campaign.products = []
          this.campaign.productsGroups = [
            {
              skus: null,
              groupId: null,
            },
          ]
          break

        case 'TRIGGER_PRODUCTS_PURCHASE_VALUE':
          this.campaign.minimumTriggerProductsValue = 0
          break

        case 'TRIGGER_PRODUCTS_QUANTITY':
          this.campaign.minimumValue = 0
          this.campaign.minimumTriggerProductsValue = 0
          break

        case 'TRIGGER_PRODUCTS_VALUE':
          this.campaign.minimumValue = 0
          this.campaign.minimumProducts = 0
          break
      }
    },
    async getData(id) {
      const formatFloatToString = (value) => {
        if (Number.isInteger(value)) {
          return value.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        }
        return value.toFixed(2).replace('.', ',')
      }

      const safeMap = (arr, fn) => (Array.isArray(arr) ? arr.map(fn) : [])

      try {
        this.isLoadingCampaign = true

        const {
          data: { findBenefitById },
        } = await this.$apollo.mutate({
          mutation: QUERY_FIND_BENEFIT_BY_ID,
          fetchPolicy: 'no-cache',
          variables: {
            id,
            franchise: this.isFranchise,
          },
          context: {
            uri: this.$microservicesUrls['benefitsManager'],
          },
        })

        const { status, _id, segments } = findBenefitById

        this.benefitId = _id
        this.segmented = !this.$getAllowedModules()['mobile'] || !!segments?.length

        const disabled = status === 'ACTIVE'

        const products = safeMap(findBenefitById.products, (item) => ({
          ...item,
          disabled,
        }))

        const productsGroups = safeMap(findBenefitById.productsGroups, (item) => ({
          ...item,
          disabled,
          isGroup: true,
          _id: item.id,
          products: safeMap(item.products, (product) => ({
            ...product,
            disabled,
          })),
        }))

        this.campaign = {
          ...findBenefitById,
          startDate: findBenefitById.startDate?.substring(0, 10) ?? '',
          expiresAt: findBenefitById.expiresAt?.substring(0, 10) ?? '',
          products: [...products, ...productsGroups],
          minimumValue: formatFloatToString(findBenefitById.minimumValue),
          value: formatFloatToString(findBenefitById.value),
          minimumTriggerProductsValue: formatFloatToString(findBenefitById.minimumTriggerProductsValue ?? 0),
        }
      } catch (error) {
        this.$snackbar({
          message: 'Erro ao buscar dados no servidor',
          snackbarColor: 'error',
        })
        this.returnToCampaigns()
      } finally {
        this.isLoadingCampaign = false
      }
    },
    computedExpiresAt(value) {
      this.campaign.expiresAt = value
    },
    async saveCampaign(draft = false) {
      this.tryingToSaveAsDraft = draft

      this.$v.$reset()
      this.$v.$touch()

      if (this.$v.$error) {
        this.$snackbar({
          message: 'Preencha todos os campos corretamente',
          snackbarColor: 'error',
        })

        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })

        return
      }

      if (typeof this.validCoupon === 'boolean' && !this.validCoupon) return

      if (!draft && this.campaign.products.length === 0 && this.campaign.promoType !== 'NO_TRIGGER_PRODUCTS') {
        this.$snackbar({ message: 'É necessário adicionar ao menos um produto para ativar ou agendar a campanha', snackbarColor: 'error' })
        return
      }

      try {
        this.loading = true
        this[draft ? 'loadingDraft' : 'loadingActive'] = true

        const response = await this.$apollo.mutate({
          mutation: draft ? UPSERT_DRAFT_BENEFIT : UPSERT_BENEFIT,
          fetchPolicy: 'no-cache',
          variables: {
            input: {
              ...this.filterValidFields(this.preparePayload),
              active: !draft,
            },
          },
          context: {
            uri: this.$microservicesUrls['benefitsManager'],
          },
        })

        this.benefitId = response.data?.upsertBenefit?.id

        Swal.fire({
          text: 'Campanha salva',
          icon: 'success',
          showConfirmButton: false,
          timer: 1000,
          customClass: {
            content: 'content-class',
          },
        })

        if (draft) {
          this.$router.push({ name: 'cupom-desconto' }).catch(() => {})
        } else {
          this.showCommunicateByChannel = true
        }
      } catch (error) {
        console.log(error)
        Swal.fire({
          text: 'Erro ao salvar campanha',
          icon: 'error',
          showConfirmButton: false,
          timer: 1000,
          customClass: {
            content: 'content-class',
          },
        })
      } finally {
        this.loading = false
        this[draft ? 'loadingDraft' : 'loadingActive'] = true
      }
    },
    async mutateBenefit(action, name) {
      const id = this.$route.params.id
      const confirm = this.$t(`campanhas.confirmations.${action}`)
      if (name) confirm.message = `Tem certeza que deseja remover a campanha "${name}"`
      const confirmed = await this.$confirm(confirm)
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return

      const actions = {
        unschedule: UNSCHEDULE_BENEFIT,
        finish: FINISH_ACTIVE_BENEFIT,
        delete: DELETE_BENEFIT,
      }
      this.loading = true
      await this.$apollo
        .mutate({
          mutation: actions[action],
          variables: {
            input: { id },
          },
          context: {
            uri: this.$microservicesUrls['benefitsManager'],
          },
        })
        .then(() => {
          this.loading = false
          this.returnToCampaigns()
        })
        .catch(() => {
          this.loading = false
          this.$snackbar(this.$t(`campanhas.snackbars.errors.${action}`))
        })
    },
    emitResultsFunction() {
      const route = this.$router.resolve({ name: 'cupom-desconto-resultados', params: { id: this.campaign._id } })
      window.open(route.href, '_blank')
    },
    emitHistoryFunction() {
      const route = this.$router.resolve({ name: 'cupom-historico-utilizacao', params: { id: this.campaign._id } })
      window.open(route.href, '_blank')
    },
  },
  validations() {
    const formatFloat = (value) => {
      const formattedValue = value.replace('.', '')
      return parseFloat(formattedValue.replace(',', '.'))
    }

    const endDateBiggerOrEqualThanStart = (value, vm) => {
      if (!value || !vm.startDate) return false
      const start = DateTime.fromFormat(vm.startDate, 'yyyy-MM-dd').toISO()
      const end = DateTime.fromFormat(value, 'yyyy-MM-dd').toISO()
      return end >= start
    }

    switch (this.tryingToSaveAsDraft) {
      case true:
        return {
          campaign: {
            title: {
              required,
              minLength: minLength(2),
              notOnlySpace,
            },
            ...(this.campaign.startDate && {
              startDate: {
                required,
                isFuture,
              },
            }),
            ...(formatFloat(this.campaign.minimumValue) > 0 && {
              value: {
                required,
                minValue: function (value) {
                  return formatFloat(value) > 0
                },
              },
            }),
          },
        }

      case false:
      default:
        return {
          campaign: {
            title: {
              required,
              minLength: minLength(2),
              notOnlySpace,
            },
            description: {
              required,
              minLength: minLength(2),
              notOnlySpace,
            },

            ...(this.$getAllowedModules()['mobile'] && {
              appTitle: {
                required,
                minLength: minLength(2),
                notOnlySpace,
              },
            }),
            ...(this.campaign.status !== 'ACTIVE'
              ? {
                  startDate: {
                    required,
                    isFuture,
                  },
                }
              : {
                  startDate: {
                    required,
                  },
                }),
            expiresAt: {
              required,
              isFuture,
              endDateBiggerOrEqualThanStart,
            },
            units: { required },
            ...(this.campaign.variableValidity && {
              daysToUseBenefitAfterRegistration: {
                required,
                minValue: minValue(1),
                maxValue: maxValue(30),
              },
            }),
            ...(['NO_TRIGGER_PRODUCTS', 'TRIGGER_PRODUCTS_PURCHASE_VALUE'].includes(this.campaign.promoType) && {
              minimumValue: {
                required,
                minValue: (value, vm) => {
                  return formatFloat(value) >= formatFloat(vm.value) + 1
                },
              },
            }),
            ...(['TRIGGER_PRODUCTS_QUANTITY', 'TRIGGER_PRODUCTS_PURCHASE_VALUE'].includes(this.campaign.promoType) && {
              minimumProducts: { required, minimumValue: minValue(1) },
            }),
            ...(['TRIGGER_PRODUCTS_VALUE'].includes(this.campaign.promoType) && {
              minimumTriggerProductsValue: {
                required,
                minValue: (value, vm) => {
                  return formatFloat(value) >= formatFloat(vm.value) + 1
                },
              },
            }),
            value: {
              required,
              minValue: function (value) {
                return formatFloat(value) >= 1
              },
            },
            ...(this.segmented
              ? {
                  segments: {
                    required,
                  },
                }
              : {
                  code: {
                    required,
                    minLength: minLength(4),
                    maxLength: maxLength(16),
                  },
                  limitUse: {
                    required,
                    minValue: minValue(1),
                  },
                }),
          },
        }
    }
  },
}
</script>

<style lang="scss" scoped>
.date-units-cards {
  display: flex;
  width: 100%;
  gap: 20px;
  align-items: stretch;

  > div {
    flex: 1;
  }
}

::v-deep .table-header-title {
  color: $cinza-d20 !important;
  font-weight: 700 !important;
  font-size: 20px !important;
}
</style>
